'use client';

import type { CSSProperties } from 'react';
import { Suspense, startTransition, useState } from 'react';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';

import { AnalyticEvents } from '@ps/analytics';
import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import { LeadFormTriggerActionType } from '@ps/models/lib/lead-form';
import type { CreatorInfo } from '@ps/types/creator';
import { useCurrentUserId, useLogin } from '@ps/ui/contexts/auth';
import { cn } from '@ps/ui/utils';

import { AccessGuard } from '../../access-control/components/access-guard/access-guard';
import type { BioAccessGuard } from '../../access-control/components/access-guard/guard-descriptor';
import { isDmPermitted } from '../../subscription/functions/isDmPermitted';
import { useBioUserAccess } from '../contexts/user-access/useBioUserAccess';
import {
  MainActionButtonContent,
  kMainActionButtonClassName,
  mainActionButtonInlineStyle,
} from '../main-actions/components/MainActionButtonContent';
import type { MainActionButtonViewModel } from '../main-actions/types/MainActionButtonViewModel';

const importNotSubscribedUpsell = () =>
  import('./components/NotSubscribedUpsell');

const NotSubscribedUpsell = dynamic(() =>
  importNotSubscribedUpsell().then(mod => mod.NotSubscribedUpsell),
);

/**
 * Lead-form-only access guard for the Message Me button — mirrors upstream
 * `useLeadFormAccessGuard()(LeadFormTriggerActionType.MessageMe)`. `kind: 'none'`
 * means the guard enforces ONLY a configured MessageMe lead form (and, once it
 * clears, proceeds for everyone incl. anonymous — never guest-contacts); the DM
 * subscription gating stays the button's own `isDmPermitted` check.
 */
const kMessageMeGuard: BioAccessGuard = {
  kind: 'none',
  triggers: [
    {
      type: LeadFormTriggerActionType.MessageMe,
      id: LeadFormTriggerActionType.MessageMe,
    },
  ],
};

/** The creator identity + DM gate inputs the Message Me island needs. */
export interface MessageMeContext {
  bioContext: BioContext;
  /** The creator (== the DM peer / target); `creatorInfo.id` is the target uid. */
  creatorInfo: CreatorInfo;
  /** `store.dmMinimumSubscriptionLevel` — undefined means "subscribers only". */
  dmMinimumSubscriptionLevel?: PopStoreExtendedSubscriptionLevel;
}

interface Props {
  button: MainActionButtonViewModel;
  context: MessageMeContext;
  backgroundColor: string;
  textColor: string;
  textStyle: CSSProperties;
  className?: string;
}

/**
 * The visitor "Message Me" button — a client island mirroring PopshopRN
 * `ChatButton`. The press is wrapped in the lead-form {@link AccessGuard}
 * (a configured MessageMe lead form shows first); once it clears,
 * `performOriginalPress` runs the DM gate before navigating to `/<url_code>/chat`:
 *
 *   - permitted → soft-nav to the chat (desktop soft-nav hits the intercept
 *     dialog; hard-load / mobile render the standalone page)
 *   - signed out → app-level `/login?next=<chat>` (intercepted login modal)
 *   - signed in but under-subscribed → the {@link NotSubscribedUpsell} modal
 *
 * The `/chat` route re-checks the same gate, so navigating while auth is still
 * resolving is safe (the destination decides). The guard's lead-form / guest
 * modals are lazy-loaded inside {@link AccessGuard}, so they stay out of the
 * visitor first-load bundle.
 */
export function MessageMeButton({
  button,
  context,
  backgroundColor,
  textColor,
  textStyle,
  className,
}: Props) {
  const router = useRouter();
  const login = useLogin();
  const currentUserId = useCurrentUserId();
  const { extendedSubscriptionLevel, loading } = useBioUserAccess();
  const [upsellOpen, setUpsellOpen] = useState(false);

  const { bioContext, creatorInfo, dmMinimumSubscriptionLevel } = context;
  const creatorId = creatorInfo.id;
  const chatPath = bioUrl(bioContext).chat();

  // The action once any lead form has cleared — mirrors upstream
  // `performOriginalPress`. Fires click analytics for every real press (owners
  // are filtered out before the guard), then runs the DM auth/subscription gate.
  const performOriginalPress = () => {
    AnalyticEvents.default.popstoreBioMessageMeClicked(
      { ctaType: 'button' },
      { creatorId },
    );
    // Auth / access still resolving → defer to the destination gate (parity).
    if (currentUserId === undefined || loading) {
      router.push(chatPath);
      return;
    }
    if (currentUserId === null) {
      login({ creatorInfo, next: chatPath });
      return;
    }
    if (isDmPermitted(dmMinimumSubscriptionLevel, extendedSubscriptionLevel)) {
      router.push(chatPath);
      return;
    }
    // Keep the bio painted while the lazy upsell chunk resolves (see the local
    // Suspense boundary below); a bare setState would let the suspension bubble
    // to the bio route boundary and flash a blank fallback on first open.
    startTransition(() => setUpsellOpen(true));
  };

  return (
    <AccessGuard
      storeId={creatorId}
      guard={kMessageMeGuard}
      sellerProfile={creatorInfo}
      onAccessGranted={performOriginalPress}
    >
      {(canProceed, requestAccess) => (
        <>
          <button
            type="button"
            onClick={() => {
              // The creator viewing their own bio can't DM themselves — no-op
              // (and never triggers the lead form).
              if (currentUserId === creatorId) return;
              // Gate on a configured MessageMe lead form first; once cleared,
              // `onAccessGranted` resumes `performOriginalPress`.
              if (!canProceed) {
                requestAccess();
                return;
              }
              performOriginalPress();
            }}
            onPointerEnter={importNotSubscribedUpsell}
            onPointerDown={importNotSubscribedUpsell}
            className={cn(kMainActionButtonClassName, className)}
            style={mainActionButtonInlineStyle({
              backgroundColor,
              textColor,
              textStyle,
            })}
          >
            <MainActionButtonContent
              type={button.type}
              label={button.label}
              textColor={textColor}
              leftIcon={button.leftIcon}
            />
          </button>
          {upsellOpen && (
            <Suspense fallback={null}>
              <NotSubscribedUpsell
                open={upsellOpen}
                onOpenChange={setUpsellOpen}
                bioContext={bioContext}
                creatorName={creatorInfo.name}
                creatorAvatar={creatorInfo.avatar}
                dmMinimumSubscriptionLevel={dmMinimumSubscriptionLevel}
              />
            </Suspense>
          )}
        </>
      )}
    </AccessGuard>
  );
}
